// Persistent bottom-center dock with phone + mail icons.
// Visible across all rooms in HouseScene. Mounted once, suspended (hidden) while
// dialogue/modal/phone/mail overlays are open so the dock doesn't compete for the eye.

import { PhoneSystem } from './PhoneSystem.js';
import { MailSystem, hasUnreadMail } from './MailSystem.js';

const LAST_VISIT_KEY = 'ian_world_last_visit';

export class DockSystem {
  constructor({ onOpen, onClose } = {}) {
    this.root = null;
    this.phoneBtn = null;
    this.mailBtn = null;
    this.mailBadge = null;
    this.suspended = false;
    this.onOpen = onOpen ?? null;
    this.onClose = onClose ?? null;

    this.phone = new PhoneSystem();
    this.mail = new MailSystem();

    this._mount();
    this._stampVisit();
    this._refreshMailBadge();
  }

  _mount() {
    const existing = document.querySelector('.iw-dock');
    if (existing) {
      this.root = existing;
      this.phoneBtn = existing.querySelector('.iw-dock-phone');
      this.mailBtn = existing.querySelector('.iw-dock-mail');
      this.mailBadge = existing.querySelector('.iw-dock-mail-badge');
      this.root.style.display = 'flex';
      this._bindClicks();
      return;
    }

    const root = document.createElement('div');
    root.className = 'iw-dock';
    root.style.cssText = `
      position: fixed;
      left: 50%;
      bottom: 18px;
      transform: translateX(-50%);
      display: flex;
      gap: 14px;
      padding: 8px 14px;
      background: rgba(15, 12, 10, 0.78);
      border: 1px solid rgba(244, 234, 213, 0.18);
      border-radius: 14px;
      box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
      backdrop-filter: blur(4px);
      z-index: 35;
      pointer-events: auto;
    `;

    const phoneBtn = this._makeButton('phone', '📱');
    phoneBtn.classList.add('iw-dock-phone');
    phoneBtn.setAttribute('aria-label', 'Open phone');

    const mailWrap = document.createElement('div');
    mailWrap.style.cssText = 'position: relative;';
    const mailBtn = this._makeButton('mail', '✉');
    mailBtn.classList.add('iw-dock-mail');
    mailBtn.setAttribute('aria-label', 'Open mailbox');

    const mailBadge = document.createElement('span');
    mailBadge.className = 'iw-dock-mail-badge';
    mailBadge.style.cssText = `
      position: absolute;
      top: 2px;
      right: 2px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #D85A30;
      box-shadow: 0 0 0 2px rgba(15, 12, 10, 0.95);
      display: none;
    `;

    mailWrap.appendChild(mailBtn);
    mailWrap.appendChild(mailBadge);

    root.appendChild(phoneBtn);
    root.appendChild(mailWrap);
    document.body.appendChild(root);

    this.root = root;
    this.phoneBtn = phoneBtn;
    this.mailBtn = mailBtn;
    this.mailBadge = mailBadge;

    this._bindClicks();
  }

  _makeButton(kind, fallbackGlyph) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.style.cssText = `
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(244, 234, 213, 0.06);
      border: 1px solid rgba(244, 234, 213, 0.2);
      border-radius: 10px;
      color: #f4ead5;
      font-size: 22px;
      cursor: pointer;
      transition: background 140ms ease, transform 140ms ease;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      padding: 0;
    `;
    btn.textContent = fallbackGlyph;
    btn.addEventListener('mouseenter', () => {
      btn.style.background = 'rgba(244, 234, 213, 0.15)';
      btn.style.transform = 'translateY(-2px)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'rgba(244, 234, 213, 0.06)';
      btn.style.transform = 'translateY(0)';
    });
    return btn;
  }

  _bindClicks() {
    // Avoid double-binding on remount
    this.phoneBtn.onclick = () => this._open(() => this.phone.open({ onClose: () => this._afterClose() }));
    this.mailBtn.onclick = () => this._open(() => this.mail.open({
      onClose: () => {
        this._refreshMailBadge();
        this._afterClose();
      },
    }));
  }

  _open(openFn) {
    if (this.suspended) return;
    this.onOpen?.();
    this.root.style.display = 'none';
    openFn();
  }

  _afterClose() {
    if (!this.suspended) {
      this.root.style.display = 'flex';
    }
    this.onClose?.();
  }

  _refreshMailBadge() {
    // Always compare against the *previous* session's visit time, even after we've
    // overwritten LAST_VISIT_KEY with the current session timestamp.
    this.mailBadge.style.display = hasUnreadMail(this._cachedLastVisit) ? 'block' : 'none';
  }

  _stampVisit() {
    // Cache the previous visit time BEFORE we overwrite it, so badge + visibility logic
    // for this whole session compares against the previous load, not "now."
    this._cachedLastVisit = this._readLastVisit();
    try {
      window.localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
    } catch (_) {}
  }

  _readLastVisit() {
    try {
      return window.localStorage.getItem(LAST_VISIT_KEY);
    } catch (_) {
      return null;
    }
  }

  setSuspended(value) {
    this.suspended = value;
    if (value) {
      this.root.style.display = 'none';
    } else if (!this.phone.isOpen && !this.mail.isOpen) {
      this.root.style.display = 'flex';
    }
  }

  // Public API for opening the mail panel from outside the dock UI itself.
  // Used by the in-studio mailbox object so it routes through the same
  // suspend/resume + read-state-refresh flow as the dock button.
  openMail() {
    this._open(() => this.mail.open({
      onClose: () => {
        this._refreshMailBadge();
        this._afterClose();
      },
    }));
  }

  // Position the dock so its top sits a small gap below `roomBottomViewportY`.
  // Called by HouseScene whenever a room is built or the window is resized.
  alignToRoomBottom(roomBottomViewportY) {
    if (!this.root) return;
    const gap = 18;
    const fromBottom = Math.max(8, window.innerHeight - roomBottomViewportY - gap);
    this.root.style.bottom = `${fromBottom}px`;
  }
}
